import { useCallback } from 'react';

import qs from 'qs';

import { getChangePagePath } from '@change-corgi/core/security';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useQueryParamValue } from '#src/app/shared/hooks/location';

type Result = (options?: { additionalQueryParams?: Record<string, string> }) => string;

/**
 * Whether a `redirectTo`/`redirect_to` query param is actually present.
 *
 * Useful to distinguish "no redirect requested" from an explicit redirect that
 * happens to resolve to "/" (e.g. `redirect_to=/`) — `useGetRedirectUrl()`'s
 * return value alone can't tell those apart, since both normalize to "/".
 */
export function useHasRedirectUrlParam(): boolean {
	const redirectUrl1 = useQueryParamValue('redirectTo');
	const redirectUrl2 = useQueryParamValue('redirect_to');
	return redirectUrl1 !== undefined || redirectUrl2 !== undefined;
}

export function useGetRedirectUrl(): Result {
	const { resolveUrl } = useUtilityContext();
	const redirectUrl1 = useQueryParamValue('redirectTo');
	const redirectUrl2 = useQueryParamValue('redirect_to');
	const redirectUrl = redirectUrl1 || redirectUrl2 || '/';
	// the result feeds an SSR `<Redirect>` (Location header) and localStorage, so forward the normalised path
	// rather than the raw string
	const safeRedirectUrl = getChangePagePath(redirectUrl, { baseUrl: resolveUrl('/') }) ?? '/';

	return useCallback(
		({ additionalQueryParams } = { additionalQueryParams: undefined }) => {
			const [path, search] = safeRedirectUrl.split('?');

			const query = qs.parse(search ?? '');

			// Remove tokens from the query params
			const filteredQueryParams = Object.fromEntries(
				Object.entries(query).filter(([key]) => key !== 'tk' && !key.endsWith('_tk')),
			);

			return `${path}${qs.stringify(
				{
					...filteredQueryParams,
					...(additionalQueryParams || {}),
				},
				{ addQueryPrefix: true },
			)}`;
		},
		[safeRedirectUrl],
	);
}
